import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import {
  AvailableDays,
  DEFAULT_WEEKEND_DAYS,
  DEFAULT_WEEK_DAYS,
  SpaceReservationItem,
  getSpaceReservationDetail,
} from "../../../../services/spaceReservationServices";

import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import CommonButton from "../../../../components/Button";
import SpaceBadge from "../../../../components/Badge/spaceBadge";
import TimeTable from "../../../../components/SpaceReservation/TimeTable";
import BottomButtonGroup from "../../../../components/Button/Bottom";
import {
  RESERVATION_SPACE_TYPES,
  convertReservationTypeToLabel,
} from "../../../../types/reservation";

type Props = {};

export default function SpaceReservationDetail(props: Props) {
  const navigate = useNavigate();
  const params = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [detail, setDetail] = useState<Partial<SpaceReservationItem>>({});

  const location = useLocation();

  useEffect(() => {
    const loadDetail = async () => {
      if (!params.id || !params.date) return;
      setIsLoading(true);
      try {
        const { status, data } = await getSpaceReservationDetail({
          reservationSpaceId: params.id,
          reservationDate: params.date,
        });
        if (status === HttpStatusCode.Ok) {
          setDetail(data);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadDetail();
  }, [params.date, params.id]);

  const getWorkingDayString = () => {
    const isEqualAvailableDays = (
      days: AvailableDays | undefined,
      target: AvailableDays
    ) => {
      if (days === undefined) return false;
      if (target === undefined) return false; // target 체크를 안하면 테스트시 오류가 나는데 명확한 원인을 찾지 못함
      if (days.monday !== target.monday) return false;
      if (days.tuesday !== target.tuesday) return false;
      if (days.wednesday !== target.wednesday) return false;
      if (days.thursday !== target.thursday) return false;
      if (days.friday !== target.friday) return false;
      if (days.saturday !== target.saturday) return false;
      if (days.sunday !== target.sunday) return false;
      return true;
    };

    if (isEqualAvailableDays(detail.availableDays, DEFAULT_WEEK_DAYS)) {
      return "평일";
    } else if (
      isEqualAvailableDays(detail.availableDays, DEFAULT_WEEKEND_DAYS)
    ) {
      return "주말";
    } else {
      let str = [];
      const days: AvailableDays | undefined = detail.availableDays;

      if (days === undefined) return "";

      if (detail.availableDays?.monday) str.push("월");
      if (detail.availableDays?.tuesday) str.push("화");
      if (detail.availableDays?.thursday) str.push("수");
      if (detail.availableDays?.wednesday) str.push("목");
      if (detail.availableDays?.friday) str.push("금");
      if (detail.availableDays?.saturday) str.push("토");
      if (detail.availableDays?.sunday) str.push("일");

      return (
        <span dangerouslySetInnerHTML={{ __html: str.join("&#183;") }}></span>
      );
    }
  };

  const handleConfirm = () => {
    navigate(`/service/space-reservation/create/${detail.reservationSpaceId}`, {
      state: {
        parentUrl: `/service/space-reservation/detail/${params.id}/${params.date}`,
        parentState: location?.state?.parentState,
        usePayment: detail.usePayment,
        date: location?.state?.parentState?.date,
        reservationSpaceType: detail.reservationSpaceType,
      },
    });
    window.scrollTo(0, 0);
  };

  const getSurchargeDaysText = (days: AvailableDays): string => {
    const dayMap: { [key: string]: string } = {
      monday: "월",
      tuesday: "화",
      wednesday: "수",
      thursday: "목",
      friday: "금",
      saturday: "토",
      sunday: "일",
    };

    const falseDays = Object.keys(days)
      .filter((day) => !days[day as keyof AvailableDays])
      .map((day) => dayMap[day]);

    return falseDays.join("/");
  };

  return (
    <>
      {loadingComponent}
      {detail.fileInfo?.items !== undefined &&
      detail.fileInfo?.items.length > 0 ? (
        detail.fileInfo?.items
          ?.filter((file) => file.scale === "medium")
          .map((file, index) => (
            <StickyHeader
              key={index}
              className="px-5 pb-[160px]"
              title=""
              image={file.url}
              style={{
                backgroundImage: `url(${file.url})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "100% auto",
                backgroundPosition: "center top",
              }}
              onBackClick={() =>
                navigate(
                  location?.state?.parentUrl ?? "/service/space-reservation",
                  {
                    state: {
                      ...location?.state?.parentState,
                    },
                  }
                )
              }
            />
          ))
      ) : (
        <StickyHeader
          className="px-5"
          title=""
          onBackClick={() =>
            navigate(
              location?.state?.parentUrl ?? "/service/space-reservation",
              {
                state: {
                  ...location?.state?.parentState,
                },
              }
            )
          }
        />
      )}
      <div className="px-5">
        <div className="flex pt-[30px]">
          <SpaceBadge title={detail.buildingName} />
        </div>
        <div className="pb-5 font-semibold text-2xl mt-2.5 leading-[29px]">
          {detail.buildingFloorName} {detail.reservationSpaceName}
        </div>

        <HorizontalRule margin="0" className="bg-brand-neutral-05" />

        <div className="py-5">
          <TimeTable
            availableTimeStart={detail.availableTimeStart}
            availableTimeEnd={detail.availableTimeEnd}
            spaceReservationData={detail.reservationList}
          />
        </div>

        <HorizontalRule margin="0" className="bg-brand-neutral-05" />

        <div className="flex flex-col py-2">
          <div className="flex flex-row text-lg pt-5 font-medium leading-[21px] pb-2.5">
            공간 정보
          </div>
          <div className="flex flex-row bg-[#F3F3F3] px-5 p-6 gap-5 justify-between flex-wrap rounded-[3px]">
            <div className="flex flex-col">
              <div className="flex flex-row text-brand-neutral-60 text-xs leading-4 break-words whitespace-nowrap">
                {detail.usePayment === true ? (
                  <>일일 최대 이용 횟수 제한 없음</>
                ) : (
                  <>일일 최대 이용 횟수 {detail.dailyLimit || "-"} 회</>
                )}
              </div>
              <div className="flex flex-row text-base font-medium leading-[26px]">
                {getWorkingDayString()}&nbsp;
                {`${detail.availableTimeStart ?? ""} ~ ${
                  detail.availableTimeEnd ?? ""
                }`}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-row text-brand-neutral-60 text-xs leading-4 break-words whitespace-nowrap">
                최소 이용
              </div>
              <div className="flex flex-row text-base font-medium leading-[26px]">
                {detail.usableTimeStart}분
              </div>
            </div>
            {detail?.usePayment !== true && (
              <div className="flex flex-col">
                <div className="flex flex-row text-brand-neutral-60 text-xs leading-4 break-words whitespace-nowrap">
                  최대 이용
                </div>
                <div className="flex flex-row text-base font-medium leading-[26px]">
                  {detail.usableTimeEnd}분
                </div>
              </div>
            )}
          </div>
        </div>

        <HorizontalRule
          className="border-black !mt-0.5 bg-black"
          thickness={1}
          margin="0"
        />

        <div className="py-1 text-xl">
          <ReservationDetailWrapper label="시설/규모" downHorizontalRule>
            {detail.reservationSpaceFeatures || "-"} (
            {detail.availablePeopleCountStart} ~{" "}
            {detail.availablePeopleCountEnd}인)
          </ReservationDetailWrapper>

          <ReservationDetailWrapper label="공간 분류" downHorizontalRule>
            <div data-testid={"space-reservation-space-type-label"}>
              {convertReservationTypeToLabel(
                detail.reservationSpaceType ?? RESERVATION_SPACE_TYPES.DEFAULT
              )}
            </div>
          </ReservationDetailWrapper>

          {detail.usePayment === true ? (
            <>
              <ReservationDetailWrapper label="공간이용요금" downHorizontalRule>
                기본 30분당 {detail.defaultPrice}원(VAT 별도)
                <br />
                추가 30분당 {detail.additionalPrice}원(VAT 별도)
              </ReservationDetailWrapper>
              {detail.reservationSpaceType !==
                RESERVATION_SPACE_TYPES.SHOWER && (
                <ReservationDetailWrapper
                  label="할증(20%)시간"
                  downHorizontalRule
                >
                  평일 운영시간 이외 시간,
                  <br />
                  {detail.defaultPriceDays
                    ? getSurchargeDaysText(detail.defaultPriceDays) + "/공휴일"
                    : "토/일/공휴일"}
                </ReservationDetailWrapper>
              )}
            </>
          ) : (
            <ReservationDetailWrapper label="공간이용요금" downHorizontalRule>
              무료
            </ReservationDetailWrapper>
          )}
          <ReservationDetailWrapper label="담당자">
            {detail.contactInfo}
            <a href={"tel:" + detail.contactNumber}>
              <CommonButton
                variant="tertiary"
                className="bg-[#9F9F9F] mt-2.5 !text-sm text-white px-2.5 py-[5px] !rounded-full font-medium flex gap-1.5"
              >
                <PhoneIcon />
                담당자 전화
              </CommonButton>
            </a>
          </ReservationDetailWrapper>
          {detail.reservationSpaceNote && (
            <ReservationDetailWrapper label="비고" upHorizontalRule>
              {detail.reservationSpaceNote}
            </ReservationDetailWrapper>
          )}
        </div>
        <BottomButtonGroup title="예약하기" onClick={handleConfirm} />
      </div>
    </>
  );
}

export const PhoneIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.9432 14.7798C5.66198 15.0673 0.984426 7.94562 1.63431 4.47087C2.23058 3.44625 2.99487 2.68737 4.01777 2.0874L6.24248 5.14331L5.11424 7.10274C5.11424 7.10274 5.42579 8.41079 6.59832 9.58333C7.829 10.814 9.20183 11.1903 9.20183 11.1903L11.1613 10.0621L14.3267 12.3963C13.7378 13.4484 12.9953 14.1909 11.9432 14.7798Z"
      fill="white"
    />
  </svg>
);

export type ReservationDetailWrapperProps = {
  upHorizontalRule?: boolean;
  downHorizontalRule?: boolean;
  className?: string;
  children?: React.ReactNode;
  label?: string | React.ReactNode;
};

export const ReservationDetailWrapper = ({
  upHorizontalRule,
  downHorizontalRule,
  className,
  children,
  label,
}: ReservationDetailWrapperProps) => (
  <div className={className}>
    {upHorizontalRule && <HorizontalRule margin="0" />}
    <div className="flex flex-row py-1.5 gap-2.5">
      <div className="flex min-w-[90px] text-[#6E6E70] text-sm py-3 leading-[18px] font-normal">
        {label}
      </div>
      <div className="flex flex-col text-black p-2.5 text-base font-normal leading-[26px]">
        {children}
      </div>
    </div>
    {downHorizontalRule && <HorizontalRule margin="0" />}
  </div>
);
